import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Renders server-sanitised article HTML.
 *
 * ## Why `dangerouslySetInnerHTML` is the right call here
 *
 * It is used deliberately, and the safety argument rests on where the HTML comes from
 * rather than on this component:
 *
 *  - The canonical body is a ProseMirror JSON document. Clients send JSON; **no endpoint
 *    accepts HTML**, so there is no path by which arbitrary markup enters the system.
 *  - HTML is produced exclusively by `RichTextRendererService` on the server, which
 *    escapes every text node and then runs the result through an allowlist sanitiser.
 *  - The rendered output is cached on the row, so what arrives here has already passed
 *    that boundary.
 *  - A Content Security Policy without `unsafe-inline` for scripts means even a
 *    sanitiser bypass has no permitted source to execute from.
 *
 * The alternative — parsing HTML into React elements at runtime — would add a
 * dependency, cost CPU on every render, and provide no additional protection, since the
 * input it parsed would be the same string.
 *
 * The one rule this places on future contributors: **never pass user-supplied HTML to
 * this component from anywhere but the API's rendered `bodyHtml` field.**
 */
export function RichTextBody({
  html,
  className,
}: {
  html: string | null | undefined;
  className?: string;
}): ReactNode {
  if (!html || html.trim().length === 0) {
    return (
      <p className={cn('text-body italic text-muted', className)}>
        This article does not have any content yet.
      </p>
    );
  }

  return (
    <div className={cn('prose-article', className)} dangerouslySetInnerHTML={{ __html: html }} />
  );
}
